import React, { useEffect, useRef, useState } from "react";
import axios from "../../../../utils/cmsAxios";
import { useLocation, useNavigate } from "react-router-dom";
import ConciergeSearchBar from "../../Concierge/List/components/ConciergeSearchBar";
import HistorySmsDetails from "./HistorySmsDetails";

const ITEMS_PER_PAGE = 20;
const UPLOAD_BASE_URL = "https://wizmarket.ai/uploads/";
const UPLOAD_TYPE_LABELS = {
  SINGLE_IMAGE: "이미지 단건",
  IMAGE_CAROUSEL: "이미지 + 이미지",
  MIXED_CAROUSEL: "이미지 + 동영상",
  REELS: "릴스",
};

const formatDateTime = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    const parts = new Intl.DateTimeFormat("ko-KR", {
      timeZone: "Asia/Seoul",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(d);

    const get = (type) => parts.find((p) => p.type === type)?.value;
    const yyyy = get("year");
    const mm = get("month");
    const dd = get("day");
    const hh = get("hour");
    const mi = get("minute");

    return `${yyyy}.${mm}.${dd} ${hh}:${mi}`;
  } catch (e) {
    return iso;
  }
};

const formatUploadType = (uploadType) =>
  UPLOAD_TYPE_LABELS[String(uploadType || "").trim().toUpperCase()] || "-";

const ConciergeHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const restoredListStateRef = useRef(location.state?.historyListState || {});
  const restoredPage = Number(restoredListStateRef.current.page || 1);
  const initialPageRef = useRef(
    Number.isFinite(restoredPage) && restoredPage > 0 ? restoredPage : 1
  );
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialPageRef.current);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const lastFiltersRef = useRef(restoredListStateRef.current.filters || {});

  const fetchList = async (override = {}, page = 1) => {
    try {
      setLoading(true);
      setError("");
      setExpandedHistoryId(null);

      const params = {
        ...lastFiltersRef.current,
        ...override,
        page,
        limit: ITEMS_PER_PAGE,
      };

      const { data } = await axios.get(
        `${process.env.REACT_APP_FASTAPI_ADS_URL}/select/concierge/history/list`,
        { params }
      );

      setItems(data.items || []);
      setTotalCount(data.total_count || 0);
      setCurrentPage(page);
    } catch (err) {
      console.error("[admin/concierge/history/list] error:", err);
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "컨시어지 히스토리 조회 중 오류가 발생했습니다."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(lastFiltersRef.current, initialPageRef.current);
  }, []);

  const handleSearchFilters = (filters) => {
    lastFiltersRef.current = filters || {};
    fetchList(filters, 1);
  };

  const handlePageChange = (page) => {
    fetchList({}, page);
  };

  const handleStoreDetail = (userId) => {
    if (!userId) return;
    const returnTo = `${location.pathname}${location.search}`;
    navigate(`/concierge/${userId}?return_to=${encodeURIComponent(returnTo)}`, {
      state: {
        returnTo,
        returnSource: "history",
        returnState: {
          historyListState: {
            page: currentPage,
            filters: { ...lastFiltersRef.current },
          },
        },
      },
    });
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endIndex = Math.min(currentPage * ITEMS_PER_PAGE, totalCount);

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="mx-auto mt-6 max-w-[1280px] rounded-2xl bg-white shadow">
        <div className="flex">
          <div className="min-w-0 flex-1 bg-neutral-50/70">
            <main className="flex w-full flex-col p-4 pt-6">
              <ConciergeSearchBar
                title="컨시어지 히스토리"
                onSearch={handleSearchFilters}
                fetchList={fetchList}
                initialFilters={lastFiltersRef.current}
                itemsCount={items.length}
              />

              {error && (
                <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                  {error}
                </div>
              )}

              {loading ? (
                <div className="py-10 text-center text-sm text-neutral-500">
                  로딩 중입니다...
                </div>
              ) : (
                <section className="w-full overflow-x-auto px-4 pb-4">
                  {items.length === 0 ? (
                    <div className="py-10 text-center text-sm text-neutral-500">
                      발행된 컨시어지 히스토리가 없습니다.
                    </div>
                  ) : (
                    <table className="min-w-full border-t border-neutral-200 text-sm">
                      <thead className="bg-neutral-50">
                        <tr>
                          <th className="border-b px-2 py-2 text-center text-xs text-neutral-500">시작일</th>
                          <th className="border-b px-2 py-2 text-center text-xs text-neutral-500">종료일</th>
                          <th className="border-b px-2 py-2 text-center text-xs text-neutral-500">업로드 타입</th>
                          <th className="border-b px-2 py-2 text-center text-xs text-neutral-500">생성일</th>
                          <th className="border-b px-2 py-2 text-center text-xs text-neutral-500">가게 이름</th>
                          <th className="border-b px-2 py-2 text-center text-xs text-neutral-500">이미지</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {items.map((item) => (
                          <React.Fragment key={item.id}>
                          <tr className="cursor-pointer odd:bg-neutral-50/40 hover:bg-neutral-100"
                            tabIndex={0} aria-expanded={expandedHistoryId === item.id}
                            aria-controls={`history-sms-${item.id}`}
                            onClick={() => setExpandedHistoryId((id) => id === item.id ? null : item.id)}
                            onKeyDown={(event) => {
                              if (event.target === event.currentTarget && ["Enter", " "].includes(event.key)) {
                                event.preventDefault();
                                setExpandedHistoryId((id) => id === item.id ? null : item.id);
                              }
                            }}>
                            <td className="whitespace-nowrap border-b px-2 py-2 text-center text-xs text-neutral-700">
                              {formatDateTime(item.approved_at)}
                            </td>
                            <td className="whitespace-nowrap border-b px-2 py-2 text-center text-xs text-neutral-700">
                              {formatDateTime(item.expiration_date)}
                            </td>
                            <td className="whitespace-nowrap border-b px-2 py-2 text-center text-sm text-neutral-700">
                              {formatUploadType(item.upload_type)}
                            </td>
                            <td className="whitespace-nowrap border-b px-2 py-2 text-center text-xs text-neutral-700">
                              {formatDateTime(item.created_at)}
                            </td>
                            <td className="whitespace-nowrap border-b px-2 py-2 text-center text-sm text-neutral-700">
                              <button
                                type="button"
                                onClick={(event) => { event.stopPropagation(); handleStoreDetail(item.user_id); }}
                                className="font-medium text-blue-600 underline-offset-2 hover:text-blue-800 hover:underline"
                              >
                                {item.store_name}
                              </button>
                            </td>
                            <td className="whitespace-nowrap border-b px-2 py-2 text-center text-xs text-neutral-700">
                              {item.image_path ? (
                                <div className="flex justify-center">
                                  <img
                                    src={`${UPLOAD_BASE_URL}${item.image_path}`}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      if (item.instagram_url) {
                                        window.open(item.instagram_url, "_blank", "noopener,noreferrer");
                                      }
                                    }}
                                    title={item.instagram_url ? "인스타그램 업로드 URL 열기" : undefined}
                                    alt={item.register_tag || item.store_name || "히스토리 이미지"}
                                    className={`h-20 w-20 rounded-md object-cover ${
                                      item.instagram_url ? "cursor-pointer hover:opacity-80" : ""
                                    }`}
                                  />
                                </div>
                              ) : (
                                <span className="text-neutral-400">이미지 없음</span>
                              )}
                              <button type="button" className="mt-2 text-xs text-blue-600 hover:underline"
                                aria-expanded={expandedHistoryId === item.id} aria-controls={`history-sms-${item.id}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setExpandedHistoryId((id) => id === item.id ? null : item.id);
                                }}>{expandedHistoryId === item.id ? "문자 정보 닫기" : "문자 정보 보기"}</button>
                            </td>
                          </tr>
                          {expandedHistoryId === item.id && <tr><td colSpan={6} className="border-b border-neutral-200 bg-neutral-50 p-4">
                            <HistorySmsDetails historyId={item.id} formatDateTime={formatDateTime} />
                          </td></tr>}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {totalPages > 0 && (
                    <div className="mt-4 flex items-center justify-between px-2">
                      <div className="text-xs text-neutral-500">
                        {totalCount > 0 ? (
                          <span>
                            {startIndex}-{endIndex} / 총 {totalCount}건
                          </span>
                        ) : (
                          <span>총 0건</span>
                        )}
                      </div>

                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage <= 1 || loading}
                          className={`rounded border px-2 py-1 text-xs ${
                            currentPage <= 1
                              ? "cursor-not-allowed bg-neutral-100 text-neutral-400"
                              : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"
                          }`}
                        >
                          이전
                        </button>

                        <div className="flex gap-1">
                          {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter((page) => {
                              const isNearCurrent = Math.abs(page - currentPage) <= 2;
                              const isFirst = page === 1;
                              const isLast = page === totalPages;
                              return isNearCurrent || isFirst || isLast;
                            })
                            .map((page, idx, arr) => {
                              const prevPage = arr[idx - 1];
                              const showEllipsis = prevPage && page - prevPage > 1;

                              return (
                                <React.Fragment key={page}>
                                  {showEllipsis && (
                                    <span className="px-2 py-1 text-xs text-neutral-400">...</span>
                                  )}
                                  <button
                                    onClick={() => handlePageChange(page)}
                                    disabled={loading}
                                    className={`min-w-[28px] rounded border px-2 py-1 text-xs ${
                                      currentPage === page
                                        ? "border-emerald-600 bg-emerald-600 text-white"
                                        : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"
                                    }`}
                                  >
                                    {page}
                                  </button>
                                </React.Fragment>
                              );
                            })}
                        </div>

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage >= totalPages || loading}
                          className={`rounded border px-2 py-1 text-xs ${
                            currentPage >= totalPages
                              ? "cursor-not-allowed bg-neutral-100 text-neutral-400"
                              : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"
                          }`}
                        >
                          다음
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              )}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConciergeHistory;
